import React from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UsersTab from '../components/admin/UsersTab';
import CustomersTab from '../components/admin/CustomersTab';
import LicensesTab from '../components/admin/LicensesTab';
import AnalyticsTab from '../components/admin/AnalyticsTab';

const TAB_KEYS = ['users', 'customers', 'licenses', 'analytics'] as const;
type TabKey = (typeof TAB_KEYS)[number];

function parseTab(raw: string | null): TabKey {
  return (TAB_KEYS as readonly string[]).includes(raw ?? '') ? (raw as TabKey) : 'users';
}

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: { xs: 2, md: 3 } }}>
      {value === index && children}
    </Box>
  );
}

export default function AdminPage() {
  const [params, setParams] = useSearchParams();
  const { t } = useTranslation('admin');
  const activeTab = parseTab(params.get('tab'));
  const tabIndex = TAB_KEYS.indexOf(activeTab);

  const handleTabChange = (_: React.SyntheticEvent, nextIndex: number) => {
    const nextKey = TAB_KEYS[nextIndex];
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', nextKey);
      // Drop tab-scoped params so leftover state from other tabs doesn't bleed across.
      if (nextKey !== 'users') next.delete('userId');
      if (nextKey !== 'analytics') {
        for (const key of ['view', 'actorId', 'eventName', 'category', 'customerId', 'screen', 'since', 'until', 'preset', 'compare', 'hideLegacy', 'excludeEvents']) {
          next.delete(key);
        }
      }
      return next;
    }, { replace: false });
  };

  // Analytics needs more horizontal room than the other admin tabs (wide tables,
  // 5 KPI tiles, 4-up funnel grid). Other tabs stay capped at 1100 for readability.
  const containerMaxWidth = activeTab === 'analytics' ? 1600 : 1100;

  return (
    <Box sx={{ maxWidth: containerMaxWidth, mx: 'auto', px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 2, md: 4 } }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        {t('panel.title')}
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabIndex} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label={t('tabs.users')} />
          <Tab label={t('tabs.customers')} />
          <Tab label={t('tabs.licenses')} />
          <Tab label={t('tabs.analytics')} />
        </Tabs>
      </Box>

      <TabPanel value={tabIndex} index={0}>
        <UsersTab />
      </TabPanel>
      <TabPanel value={tabIndex} index={1}>
        <CustomersTab />
      </TabPanel>
      <TabPanel value={tabIndex} index={2}>
        <LicensesTab />
      </TabPanel>
      <TabPanel value={tabIndex} index={3}>
        <AnalyticsTab />
      </TabPanel>
    </Box>
  );
}
